import { createNoise3D } from "simplex-noise"
import type { ColorScheme } from "../brand"
import { Bounds } from "./bounds"
import { Paths } from "./paths"
import { Random } from "./utils"

export interface ProcessorArgs {
  size: { rows: number; cols: number }
  seed: number
  warp: number
  shapeStyles: ShapeStyle[]
  stickyStyles?: boolean
  animatedStyling: boolean
  scheme: ColorScheme
  transparent?: boolean

  density: number
  voids: number
  downsample: number
  sharpness: number

  lineOffset: number

  targetShape?: number
}

type Cell = {
  layerId: number
  isEdge?: boolean
  readonly x: number
  readonly y: number
  readonly id: number
  noGrow?: boolean
}

enum Edge {
  Left,
  Right,
  Up,
  Down,
}

const EDGE_DIRECTION = {
  [Edge.Left]: [-1, 0],
  [Edge.Right]: [1, 0],
  [Edge.Up]: [0, -1],
  [Edge.Down]: [0, 1],
} satisfies {
  [K in Edge]: [number, number]
}

const CORNER_TYPES = {
  tl: {
    offset: [0, 0],
    angle: -Math.PI * 0,
  },
  tr: {
    offset: [1, 0],
    angle: -Math.PI * 0.5,
  },
  br: {
    offset: [1, 1],
    angle: -Math.PI * 1,
  },
  bl: {
    offset: [0, 1],
    angle: -Math.PI * 1.5,
  },
} satisfies {
  [K in CornerType]: {
    offset: [number, number]
    angle: number
  }
}

const EDGES = [Edge.Left, Edge.Right, Edge.Up, Edge.Down]

const noise = createNoise3D(Random.value)

type CornerType = "tl" | "tr" | "br" | "bl"

type CellPath = {
  readonly x: number
  readonly y: number
  vertical: boolean
  horizontal: boolean
  corner: CornerType | null
  dx: number
  dy: number
  inner: boolean
  pos: [number, number]
  extent: [number, number]
  radius: number
}

/**
 * The preferred direction to use when tracing a shape
 * The order is important!
 */
const TRACING_VECTORS = [
  [+1, +0], // R
  [+0, +1], // B
  [-1, +0], // L
  [+0, -1], // T
  [+1, +1], // BR
  [-1, +1], // BL
  [-1, -1], // TL
  [+1, -1], // TR
] as const

export type ShapeStyle = {
  fill: string
  stroke: string
  strokeWidth: number
}

export class Shape {
  private _tracedPath?: CellPath[]
  private _bounds?: Bounds

  /**
   * Style applied just to this shape
   * It's best to use this for still images
   **/
  style: ShapeStyle = {
    fill: "none",
    stroke: "black",
    strokeWidth: 3,
  }

  /**
   * Style applied to this layerId
   * It's best to use this for animated layouts, to prevent style popping
   **/
  layerStyle: ShapeStyle = {
    fill: "none",
    stroke: "black",
    strokeWidth: 3,
  }

  constructor(
    public layout: Processor,
    public layerId: number,
    public cells: Cell[],
  ) {}

  getEdgeCells(edge: Edge) {
    const dir = EDGE_DIRECTION[edge]
    return this.cells.filter((cell) => {
      let x = cell.x + dir[0]
      let y = cell.y + dir[1]
      const hasCell = this.cells.some((c) => c.x === x && c.y === y)
      return !hasCell
    })
  }

  expandEdge(edge: Edge, ifMatch?: (cell: Cell) => boolean) {
    const edgeCells = this.getEdgeCells(edge)
    const dir = EDGE_DIRECTION[edge]
    const targetCells = edgeCells.map((cell) => this.layout.cell(cell.x + dir[0], cell.y + dir[1]))
    for (const target of targetCells) {
      if (target && (!ifMatch || ifMatch(target))) {
        target.layerId = this.layerId
        // target.noGrow = edgeCells[0].noGrow;
      }
    }
  }

  getOuterCells() {
    const dirs = TRACING_VECTORS

    for (const cell of this.cells) {
      const x = cell.x
      const y = cell.y
      cell.isEdge = !dirs.every((dir) => {
        const target = this.layout.cell(x + dir[0], y + dir[1])
        return target && target.layerId === this.layerId
      })
    }

    let current = this.cells[0]
    const cells: Cell[] = []
    cells.push(current)

    while (true) {
      let nextCell: Cell | undefined
      for (const dir of dirs) {
        nextCell = this.layout.cell(current.x + dir[0], current.y + dir[1])
        if (!nextCell || nextCell.layerId !== this.layerId || !nextCell.isEdge || cells.includes(nextCell)) {
          nextCell = undefined
        }
        if (nextCell) break
      }
      if (!nextCell) break
      cells.push(nextCell)
      current = nextCell
    }
    return cells
  }

  get tracedPath(): CellPath[] {
    if (this._tracedPath) return this._tracedPath
    const now = performance.now()
    const cells = this.getOuterCells()

    const path: CellPath[] = []

    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i]
      path.push({
        x: cell.x,
        y: cell.y,
        corner: null,
        inner: false,
        vertical: false,
        horizontal: false,
        dx: 0,
        dy: 0,
        extent: [0, 0],
        radius: 0,
        pos: [0, 0],
      })
    }

    if (path.length === 0) {
      return []
    }

    // path.push(path[0]);

    let extent = 0
    let lastCorner: CellPath | undefined
    for (let i = 0; i < path.length + 1; i++) {
      let a = path[(i - 1 + path.length) % path.length]
      let b = path[i % path.length]
      let c = path[(i + 1 + path.length) % path.length]
      const dx = c.x - a.x
      const dy = c.y - a.y
      const cross = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x)
      const isClockwise = cross > 0
      // if (cross > 0) return Orientation.CounterClockwise;
      // else if (cross < 0) return Orientation.Clockwise;
      // b.corner = dx + "," + dy;
      b.dx = dx
      b.dy = dy
      if (isClockwise) {
        if (dx === 1 && dy === -1) b.corner = "tl"
        if (dx === -1 && dy === -1) b.corner = "bl"
        if (dx === -1 && dy === 1) b.corner = "br"
        if (dx === 1 && dy === 1) b.corner = "tr"
      } else {
        b.inner = true
        if (dx === 1 && dy === -1) b.corner = "br"
        if (dx === -1 && dy === -1) b.corner = "tr"
        if (dx === -1 && dy === 1) b.corner = "tl"
        if (dx === 1 && dy === 1) b.corner = "bl"
      }
      if (!b.corner || isClockwise) {
        extent++
      }
      if (!b.corner) {
        b.vertical = dx === 0
        b.horizontal = dy === 0
      } else {
        if (lastCorner) lastCorner.extent[1] = extent
        b.extent[0] = extent
        extent = 0
        lastCorner = b
      }

      if (b.corner) {
        let { offset, angle } = CORNER_TYPES[b.corner!]
        if (b.inner) {
          offset = [1 - offset[0], 1 - offset[1]]
          angle = angle - Math.PI * 1.5
        }
        b.pos[0] = b.x + offset[0]
        b.pos[1] = b.y + offset[1]
      }
    }

    const corners = path.filter((p) => !!p.corner)
    for (let i = 0; i < corners.length; i++) {
      const p = corners[i]
      const prev = corners[(i - 1 + corners.length) % corners.length]
      const dist = Math.hypot(prev.pos[0] - p.pos[0], prev.pos[1] - p.pos[1])
      prev.extent[1] = dist
      p.extent[0] = dist
    }

    // Determine corner radius
    for (let i = 0; i < path.length; i++) {
      let cell = path[i]
      if (!cell.corner) continue
      // if (cell.inner) continue;
      let maxRadius = Math.min(cell.extent[0], cell.extent[1]) / 2
      cell.radius = maxRadius
    }

    this._tracedPath = path
    return path
  }

  get bounds() {
    if (this._bounds) return this._bounds
    const bounds = new Bounds()
    for (const cell of this.tracedPath) {
      bounds.extend(cell.x, cell.y)
    }
    this._bounds = bounds
    return bounds
  }

  getPath(cellSize: number, lineOffset = 0): Paths.Command[] {
    const corners = this.tracedPath
      .filter((p) => !!p.corner)
      .map((p) => {
        let axis = [0, 0]
        let cornerPos = [p.x, p.y]
        let { offset, angle } = CORNER_TYPES[p.corner!]
        if (p.inner) {
          offset = [1 - offset[0], 1 - offset[1]]
          angle = angle - Math.PI * 1.5
        }
        offset = [offset[0] === 0 ? lineOffset : 1 - lineOffset, offset[1] === 0 ? lineOffset : 1 - lineOffset]
        cornerPos[0] += offset[0]
        cornerPos[1] += offset[1]

        let radius = p.radius - lineOffset

        if (p.inner) radius += lineOffset

        return {
          ...p,
          cornerPos,
          cornerOffset: offset,
          radius,
          arcStart: [cornerPos[0] + Math.sin(angle + 0) * radius, cornerPos[1] + Math.cos(angle + 0) * radius],
          arcEnd: [
            cornerPos[0] + Math.sin(angle + Math.PI * 0.5 * (p.inner ? -1 : 1)) * radius,
            cornerPos[1] + Math.cos(angle + Math.PI * 0.5 * (p.inner ? -1 : 1)) * radius,
          ],
        }
      })

    const path: Paths.Command[] = []
    for (let i = 0; i < corners.length; i++) {
      const p = corners[i]

      if (path.length === 0) {
        path.push({
          type: "M",
          args: [p.arcStart[0] * cellSize, +p.arcStart[1] * cellSize],
        })
      } else {
        path.push({
          type: "L",
          args: [p.arcStart[0] * cellSize, +p.arcStart[1] * cellSize],
        })
      }
      path.push({
        type: "A",
        args: [
          cellSize * p.radius,
          cellSize * p.radius,
          1,
          0,
          p.inner ? 0 : 1,
          p.arcEnd[0] * cellSize,
          p.arcEnd[1] * cellSize,
        ],
      })
    }
    path.push({ type: "Z" })

    return path
  }
}

type PencilCell = {
  x: number
  y: number
  layerId: number
  lifetime: number
}

export class Processor {
  rows = 12
  cols = 12

  private subscribers = new Set<() => void>()
  private idCounter = 0

  grid: Cell[] = []

  shapes: Shape[] = []

  interactiveGrid: {
    cells: Cell[]
    cols: number
    rows: number
  } = {
    cells: [],
    cols: 0,
    rows: 0,
  }

  private stickyLayerStyles: Map<number, number> = new Map()

  needsUpdate = false

  constructor(public args: ProcessorArgs) {
    this.resize(args.size.cols, args.size.rows)
  }

  resize(cols: number, rows: number) {
    this.rows = Math.floor(rows)
    this.cols = Math.floor(cols)
    this.grid = []
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        const index = this.cellIndex(x, y)
        this.grid[index] = {
          id: index,
          layerId: 0,
          x,
          y,
        }
      }
    }
  }

  reset() {
    this.grid = []
    this.idCounter = 0
    this.shapes = []
    this.resize(this.args.size.cols, this.args.size.rows)
  }

  cell(x: number, y: number): Cell | undefined {
    if (x < 0 || x >= this.cols || y < 0 || y >= this.rows) return undefined
    const index = this.cellIndex(x, y)
    return this.grid[index]
  }

  cellIndex(x: number, y: number) {
    if (x < 0 || x >= this.cols || y < 0 || y >= this.rows) return -1
    return Math.floor(y) * this.cols + Math.floor(x)
  }

  getContiguousCells(baseCell: Cell): Shape {
    const visited = new Set<Cell>()
    const stack = [baseCell]
    const result = new Set<Cell>([baseCell])
    visited.add(baseCell)

    const DIRS = [
      [-1, 0],
      [1, 0],
      [0, -1],
      [0, 1],
    ]

    while (stack.length > 0) {
      const current = stack.pop()!

      for (const [dx, dy] of DIRS) {
        const nb = this.cell(current.x + dx, current.y + dy)
        if (!nb || visited.has(nb) || nb.layerId !== baseCell.layerId) continue

        visited.add(nb)
        result.add(nb)
        stack.push(nb)
      }
    }

    return new Shape(this, baseCell.layerId, Array.from(result))
  }

  computeShapes(): Shape[] {
    const visited = new Set<Cell>()
    const shapes: Shape[] = []
    for (const cell of this.grid) {
      if (visited.has(cell)) continue
      const shape = this.getContiguousCells(cell)
      shapes.push(shape)
      for (const childCell of shape.cells) {
        visited.add(childCell)
      }
    }
    return shapes
  }

  divide() {
    const cells = this.grid
    this.resize(Math.round(this.cols * 2), Math.round(this.rows * 2))
    for (const oldCell of cells) {
      for (let i = 0; i < 2; i++) {
        for (let j = 0; j < 2; j++) {
          const cell = this.cell(oldCell.x * 2 + i, oldCell.y * 2 + j)!
          if (cell) {
            cell.layerId = oldCell.layerId
            cell.noGrow = oldCell.noGrow
          }
        }
      }
    }
  }

  scale(scaleX: number, scaleY = scaleX) {
    const old = {
      cells: this.grid,
      cols: this.cols,
      rows: this.rows,
    }
    const newWidth = Math.round(this.cols * scaleX)
    const newHeight = Math.round(this.rows * scaleY)
    if (newWidth === this.cols && newHeight === this.rows) {
      return false
    }
    this.resize(newWidth, newHeight)
    for (let i = 0; i < this.cols; i++) {
      for (let j = 0; j < this.rows; j++) {
        const cell = this.cell(i, j)!
        const oldCell = old.cells[Math.floor(j / scaleY) * old.cols + Math.floor(i / scaleX)]
        if (!oldCell) continue
        cell.layerId = oldCell.layerId
        cell.noGrow = oldCell.noGrow
      }
    }
    return true
  }

  warp(value: number) {
    if (value === 0) return
    const old = {
      cells: this.grid,
      cols: this.cols,
      rows: this.rows,
    }
    const globalStrength = Math.max(this.cols, this.rows) * 0.1
    this.resize(this.cols, this.rows)
    for (let i = 0; i < this.cols; i++) {
      const xEdge = Math.sin((i / (this.cols - 1)) * Math.PI)
      for (let j = 0; j < this.rows; j++) {
        const yEdge = Math.sin((j / (this.rows - 1)) * Math.PI)
        const cell = this.cell(i, j)!
        const x = i + noise(i * 0.05, j * 0.05, value) * globalStrength * xEdge
        const y = j + noise(i * 0.05 + 100, j * 0.05 + 100, value) * globalStrength * yEdge
        const oldCell = old.cells[Math.floor(y) * old.cols + Math.floor(x)]
        if (!oldCell) continue
        cell.layerId = oldCell.layerId
        cell.noGrow = oldCell.noGrow
      }
    }
  }

  downsample(factor: number) {
    this.scale(factor)
    this.scale(1 / factor)
  }

  expand(iterations = 3, layerId?: number, force = false) {
    for (let i = 0; i < iterations; i++) {
      const shapes = this.computeShapes()
      for (const shape of shapes) {
        if (shape.layerId === 0) continue
        if (layerId !== undefined && shape.layerId !== layerId) continue
        if (Random.value(0, 10) > shape.layerId) continue
        const edge = EDGES[(shape.layerId + i) % EDGES.length]
        shape.expandEdge(edge, (c) => (c.layerId === 0 && !c.noGrow) || force)
      }
    }
  }

  pruneShapes() {
    for (const shape of this.computeShapes()) {
      const outer = shape.tracedPath
      let toggleCount = 0
      let length = 0
      for (const c of outer) {
        if (c.corner) {
          length = c.extent[1]
        } else {
          length--
        }
        if (toggleCount <= 0 && Random.value() < 0.02 && (c.horizontal || c.vertical)) {
          toggleCount = Random.value(0, length * 0.9)
        } else if (toggleCount > 0) {
          const cell = this.cell(c.x, c.y)
          if (cell) {
            cell.layerId = 0
          }
        }
        toggleCount--
      }
    }
  }

  randomCells(n = 10): Cell[] {
    // Choose some random points
    const coords: [number, number][] = []
    for (let i = 0; i < n; i++) {
      coords.push([Random.value(), Random.value()])
    }

    // Spread the away from each other
    for (let i = 0; i < 50; i++) {
      for (let c1 of coords) {
        for (let c2 of coords) {
          if (c1 === c2) continue
          const dx = c1[0] - c2[0]
          const dy = c1[1] - c2[1]
          const dist = Math.hypot(dx, dy)
          const strength = Math.max(0, 1 - dist)

          c1[0] += (0.001 / dx) * strength
          c1[1] += (0.001 / dy) * strength
          c1[0] += (0.5 - c1[0]) * 0.005
          c1[1] += (0.5 - c1[1]) * 0.005

          c2[0] -= (0.001 / dx) * strength
          c2[1] -= (0.001 / dy) * strength
          c2[0] += (0.5 - c2[0]) * 0.005
          c2[1] += (0.5 - c2[1]) * 0.005
        }
      }
    }

    return coords.map(([x, y]) => this.cell(x * this.cols, y * this.rows)!).filter((c) => !!c)
  }

  spawnSeeds(n = 10) {
    for (const cell of this.randomCells(n)) {
      if (cell.layerId === 0) {
        cell.layerId = ++this.idCounter
      }
    }
  }

  spawnVoids(n = 10) {
    for (const cell of this.randomCells(n)) {
      // if (cell.layerId === 0) {
      //   cell.layerId = ++this.idCounter;
      // }
      cell.noGrow = true
      cell.layerId = 0
    }
  }

  killOrphans() {
    const cellsToKill = new Set<Cell>()
    for (const shape of this.computeShapes()) {
      for (const cell of shape.cells) {
        let neighbours = 0
        const dirs = [
          [-1, 0],
          [1, 0],
          [0, 1],
          [0, -1],
        ]
        // for (let dx = -1; dx < 2; dx++) {
        for (let [dx, dy] of dirs) {
          const c = this.cell(cell.x + dx, cell.y + dy)
          if (c?.layerId === shape.layerId && c.isEdge) neighbours++
        }
        // }
        if (cell.isEdge && neighbours > 2) {
          cellsToKill.add(cell)
        }
      }
    }
    cellsToKill.forEach((cell) => {
      cell.layerId = 0
    })
  }

  generate() {
    this.reset()
    Random.seed(this.args.seed)
    this.spawnSeeds(this.args.density)

    this.expand(9)
    this.spawnVoids(this.args.voids)

    this.scale(1.5)

    this.warp(this.args.warp)

    this.downsample(this.args.downsample)

    this.expand(100)
    Random.push()
    if (this.args.targetShape !== undefined) {
      this.expand(1, this.args.targetShape, true)
    }
    Random.pop()

    this.scale(this.args.sharpness)
    this.updateShapes()
    this.pruneShapes()
    this.updateShapes()

    const didCorrectScale = this.scale(1, this.cols / this.rows / (this.args.size.cols / this.args.size.rows))
    // console.log(
    //   "Scale",
    //   1,
    //   this.cols / this.rows / (this.args.size.cols / this.args.size.rows),
    //   didCorrectScale,
    // )
    this.scale(4)

    this.baseCells = this.grid.map((c) => c.layerId)
    this.updateDraw()
    // this.divide();

    // this.interactiveGrid = {
    //   cells: [...this.grid],
    //   cols: this.cols,
    //   rows: this.rows,
    // };
  }

  baseCells: number[] = []
  drawCells: PencilCell[] = []

  draw(col: number, row: number, layerId: number) {
    const cx = Math.floor(col / 4) * 4
    const cy = Math.floor(row / 4) * 4
    // layerId = 0;
    let changed = false
    // changed = this.cullDraw(0.1);
    for (let x = 0; x < 4; x++) {
      for (let y = 0; y < 4; y++) {
        const cell = this.cell(cx + x, cy + y)
        if (cell) {
          if (cell.layerId !== layerId) {
            changed = true
            this.drawCells.push({
              x: cx + x,
              y: cy + y,
              layerId,
              lifetime: 3,
            })
            cell.layerId = layerId
          }
        }
      }
    }
    this.updateDraw()
    return changed
    // this.scale(0.25);
    // const cx = Math.floor(col * 0.25);
    // const cy = Math.floor(row * 0.25);
    // const cell = this.cell(cx, cy);
    // if (cell) {
    //   cell.layerId = layerId;
    // }
    // this.scale(4);
  }

  cullDraw(amount = 1) {
    const total = this.drawCells.length
    this.drawCells = this.drawCells.filter((dc) => {
      dc.lifetime -= amount
      return dc.lifetime > 0
    })
    return total !== this.drawCells.length
  }

  updateDraw() {
    for (let index in this.baseCells) {
      this.grid[index].layerId = this.baseCells[index]
    }
    for (let i = this.drawCells.length - 1; i >= 0; i--) {
      const dc = this.drawCells[i]
      const cell = this.cell(dc.x, dc.y)
      if (cell) {
        if (cell.layerId !== dc.layerId) {
          cell.layerId = dc.layerId
          this.needsUpdate = true
        }
      }
    }
  }

  // interaction(func: () => void) {
  //   this.grid = [...this.interactiveGrid.cells];
  //   this.rows = this.interactiveGrid.rows;
  //   this.cols = this.interactiveGrid.cols;
  //   func();
  //   this.interactiveGrid = {
  //     cells: this.grid,
  //     cols: this.cols,
  //     rows: this.rows,
  //   };
  //   this.finalize();
  // }

  private finalizeShapes() {
    const radii = [0.75, 1, 2, 4, 8].map((size) => size * 1)

    const corners = new Map<string, number>()
    Random.push()
    for (const shape of this.shapes) {
      for (const p of shape.tracedPath) {
        Random.seed(p.x * 10 + p.y)
        if (!radii.includes(p.radius) || Random.value() < 0.2) {
          const targetRadius = Random.from(radii)
          p.radius = Math.min(p.radius, targetRadius)
        }

        if (p.corner) {
          let { offset } = CORNER_TYPES[p.corner]
          if (p.inner) {
            offset = [1 - offset[0], 1 - offset[1]]
          }
          const key = p.x + offset[0] + "," + (p.y + offset[1]) + "," + p.inner
          corners.set(key, Math.min(p.radius, corners.get(key) ?? p.radius))
        }
      }
    }
    // Inner corners mustn't have a radius any larger than opposite corners
    for (const shape of this.shapes) {
      for (const p of shape.tracedPath) {
        if (p.corner) {
          let { offset } = CORNER_TYPES[p.corner]
          if (p.inner) {
            offset = [1 - offset[0], 1 - offset[1]]
          }
          const key = p.x + offset[0] + "," + (p.y + offset[1]) + "," + !p.inner
          if (corners.has(key)) {
            const maxCorner = corners.get(key)!
            p.radius = Math.min(p.radius, maxCorner)
          }
        }
      }
    }

    Random.pop()

    // console.log("process", performance.now() - start);

    Random.push()
    this.addShapeStyles()
    Random.pop()
  }

  private addShapeStyles() {
    const styles = [...this.args.shapeStyles]

    // Index the shapes
    const shapes = Random.shuffled(this.shapes).map((shape) => ({
      shape,
      path: shape.tracedPath.map((vert) => vert.pos),
      poly: shape.tracedPath.filter((vert) => !!vert.corner).map((v) => [v.x, v.y] as [number, number]),
      cells: shape.getOuterCells(),
    }))

    // Prepare style scoring array
    let candidates = styles.map((style) => ({ style, score: 0 }))

    const layers = new Map<number, { style: ShapeStyle; score: number }[]>()

    for (const shape of shapes) {
      // Score each candidate style based on neighbouring shapes
      for (const candidate of candidates) {
        candidate.score = 1000
        for (const other of shapes) {
          if (shape === other || other.shape.style !== candidate.style) continue
          for (const pos of shape.path) {
            const dist = Paths.sdfPolygon(pos, other.poly)
            candidate.score = Math.min(candidate.score, dist)
          }
        }
      }

      // Choose the style with the lowest score
      candidates.sort((a, b) => b.score - a.score)

      if (!layers.has(shape.shape.layerId)) {
        layers.set(shape.shape.layerId, [...candidates.map((c) => ({ ...c, score: 0 }))])
      }
      for (const candidate of layers.get(shape.shape.layerId)!) {
        candidate.score += candidates.find((c) => c.style === candidate.style)!.score * shape.shape.cells.length
      }

      shape.shape.style = candidates[0].style
    }

    // Calculate layer styles
    for (const [layerId, styleShapes] of layers) {
      let style: ShapeStyle | null = null
      if (this.stickyLayerStyles.has(layerId)) {
        const styleIndex = this.stickyLayerStyles.get(layerId)!
        style = this.args.shapeStyles[styleIndex]
      }
      if (!style) {
        styleShapes.sort((a, b) => b.score - a.score)
        style = styleShapes[0].style
        if (this.args.stickyStyles) {
          this.stickyLayerStyles.set(layerId, this.args.shapeStyles.indexOf(style))
        }
      }
      for (const shape of shapes) {
        if (shape.shape.layerId === layerId) {
          shape.shape.style = style
        }
      }
    }
  }

  finalize(notify = true) {
    Random.seed(this.args.seed)
    this.updateShapes(notify)
  }

  updateShapes(notify = true) {
    this.shapes = this.computeShapes().filter((s) => s.layerId !== 0)
    this.finalizeShapes()
    if (notify) this.notify()
  }

  notify() {
    for (const cb of this.subscribers) cb()
  }

  onChange(cb: () => void) {
    this.subscribers.add(cb)
    return () => {
      this.subscribers.delete(cb)
    }
  }

  getSVGString(width: number, includeBackground = false, basePadding = 0): string {
    let svgString = ""
    const height = Math.floor((width * this.rows) / this.cols)
    const cellSize = width / this.cols

    const padding = basePadding + (this.shapes[0].style.strokeWidth * (cellSize / 20) || 0)

    svgString += `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="${-padding} ${-padding} ${width + padding * 2} ${height + padding * 2}">`

    if (includeBackground) {
      const bgColor = this.args.scheme.bgColor
      svgString += `<rect x="${-padding}" y="${-padding}" width="${width + padding * 2}" height="${height + padding * 2}" fill="${bgColor}"/>`
    }

    for (const shape of this.shapes) {
      let fill = "none"
      let stroke = "none"
      let lineWidth = 1
      if (shape.style.fill !== "none") {
        fill = shape.style.fill
      }
      if (shape.style.stroke !== "none") {
        stroke = shape.style.stroke
        lineWidth = shape.style.strokeWidth * (cellSize / 20) || 0
      }
      const pathData = shape.getPath(cellSize, this.args.lineOffset)
      const pathString = Paths.stringify(pathData)
      svgString += `<path d="${pathString}" fill="${fill}" stroke="${stroke}" stroke-width="${lineWidth}"/>`
    }

    svgString += `</svg>`

    return svgString
  }
}

export namespace Generate {
  export function main(args: ProcessorArgs, p: Processor) {}
}
